import {
  html,
  type ReactiveController,
  type ReactiveControllerHost,
  type TemplateResult,
} from "lit";
import { createRef, ref } from "lit/directives/ref.js";
import type { UploadFileItem } from "@/components/upload";
import type Upload from "@/components/upload/upload";
import { type FeedbackWidgetSubmitDetail } from "../types";

interface BugFeedbackCallbacks {
  onBack: () => void;
  onCancel: (e: Event) => void;
  onSubmit: (detail: FeedbackWidgetSubmitDetail) => void;
}

export class BugFeedbackController implements ReactiveController {
  private _bugDetails = "";
  private _bugTitle = "";
  private _onDetailsInput = (e: CustomEvent<{ value: string }>) => {
    if (typeof e.detail?.value === "string") {
      this._bugDetails = e.detail.value;
      this.host.requestUpdate();
    }
  };
  private _onSubmit = () => {
    this.callbacks.onSubmit({
      feedback: this._bugDetails,
      feedbackType: "bug",
      screenshots: this.files,
      title: this._bugTitle,
    });
  };

  private _onTitleInput = (e: CustomEvent<{ value: string }>) => {
    if (typeof e.detail?.value === "string") {
      this._bugTitle = e.detail.value;
      this.host.requestUpdate();
    }
  };

  private _pendingFocus = false;

  private _uploadRef = createRef<Upload>();

  constructor(
    private host: ReactiveControllerHost & HTMLElement,
    private callbacks: BugFeedbackCallbacks
  ) {
    host.addController(this);
  }

  hostUpdated() {
    if (this._pendingFocus) {
      this._pendingFocus = false;
      this.host.updateComplete.then(() => {
        this.host.shadowRoot?.querySelector<HTMLElement>("eko-input")?.focus();
      });
    }
  }

  render(params: { errorMessage: string | undefined; loading: boolean }): TemplateResult {
    const { errorMessage, loading } = params;
    return html`
      <eko-featured-icon
        slot="icon"
        variant="square-modern"
        theme="neutral"
        icon="alert-triangle"></eko-featured-icon>
      <span slot="title" class="eko-feedback-widget__title">Tell us what went wrong</span>

      <div class="eko-feedback-widget__form">
        ${errorMessage ? html`<eko-alert variant="danger">${errorMessage}</eko-alert>` : html``}
        <eko-input
          label="Title"
          placeholder="Short summary"
          .value=${this._bugTitle}
          ?required=${true}
          @input=${this._onTitleInput}></eko-input>
        <eko-textarea
          label="Details"
          placeholder="Tell us about what happened..."
          .value=${this._bugDetails}
          .rows=${5}
          @input=${this._onDetailsInput}></eko-textarea>
        <div class="eko-feedback-widget__screenshots">
          <span class="eko-feedback-widget__screenshots-label">Screenshots</span>
          <eko-upload
            ${ref(this._uploadRef)}
            multiple
            accept="image/*"
            max-files="3"
            .maxFileSize=${5 * 1024 * 1024}>
            <input type="file" accept="image/*" multiple />
            <span slot="hint">Attach up to 3 screenshots (max 5 MB each)</span>
          </eko-upload>
        </div>
      </div>

      <div slot="footer" class="eko-feedback-widget__footer">
        <eko-button ?disabled=${loading} @click=${this.callbacks.onBack}>Back</eko-button>
        <eko-button
          variant="primary"
          ?disabled=${!this._bugTitle || loading}
          ?loading=${loading}
          @click=${this._onSubmit}
          >Submit</eko-button
        >
      </div>
    `;
  }

  requestFocus() {
    this._pendingFocus = true;
  }

  reset() {
    this._bugTitle = "";
    this._bugDetails = "";
    this._pendingFocus = false;
  }

  get files(): UploadFileItem[] {
    return (this._uploadRef.value?.files ?? []).filter((f) => f.status !== "error");
  }
}
